/**
 * Standalone translation loader for Exolog
 * This script loads and applies scoped translations based on URL language slug
 * Works independently of the Exolog Editor
 * Uses scoped JSON files: /sites/{alias}/translations/{lang}/{scope}.{id}.{lang}.json
 */

(function () {
    "use strict";

    const ExologTranslation = {
        currentLanguage: null,

        /**
         * The Edition's primary language.
         *
         * This was hard-coded "en", and the guard in init() compares the URL's
         * language against it to decide whether a page needs translating. On an
         * nl-primary Edition that meant /en compared "en" against "en", the
         * guard never passed, and the translations were never applied - the page
         * fell back to Dutch on every load. Pim: "as soon as I refresh the page
         * it is back to dutch".
         *
         * I corrected this earlier today and reverted it, because passing the
         * guard also popped the setup modal at him unprompted. That was the
         * wrong half to give up: the modal is the thing to fix, not the language.
         */
        get defaultLanguage() {
            const params = window.ExologEditorParams || {};
            if (params.default_language) {
                return params.default_language;
            }

            const declared = window.ExologLanguages;
            if (Array.isArray(declared) && declared.length) {
                return declared[0];
            }

            return "en";
        },

        translations: {}, // Stores loaded translations by scope.id
        get supportedLanguages() {
            // The Edition's OWN languages, when the page has told us them.
            //
            // `supported` below is a global list - en, de, fr, nl, tr, es - so
            // treating it as the answer meant /tr announced "Translating
            // content..." on an Edition that has no Turkish, for a URL that was
            // about to 404. ExologLanguages is emitted per page and holds the
            // Edition's declared set, so it is the one to trust. It is also
            // available to an anonymous visitor, which editorConfig is not.
            const declared = window.ExologLanguages;
            if (Array.isArray(declared) && declared.length) {
                return declared;
            }

            // Load dynamically from editorConfig if available
            const editorConfig = window.editorConfig || {};
            return editorConfig?.data?.language?.supported || ["en", "de", "fr", "nl", "tr", "es"];
        },
        isEditMode: false, // Track if user is in edit mode
        translationScope: null, // 'page' or 'site'
        fallbackBehavior: null, // 'default' or '404'
        hasTranslationFiles: false, // Track if translation files exist
        modalDismissed: false, // Flag to prevent translations before modal is dismissed
        modalVisible: false, // Track if modal is currently visible
        initialized: false, // Prevent double initialization

        /**
         * Initialize translation system
         */
        init: function () {
            // Prevent double initialization
            if (this.initialized) {
                console.warn(
                    "⚠️ ExologTranslation already initialized, skipping"
                );
                return;
            }
            this.initialized = true;

            // Stand down where the Page JSON layer owns translations.
            //
            // On an Edition with page_json_enabled, this loader and the new
            // layer were both trying to translate the same page: it found no
            // side-files, put up its setup modal and began machine-translating,
            // which is why a German page came back with some strings translated
            // and the rest Dutch. Two systems translating one page is worse
            // than either alone.
            //
            // Editions that have not opted in are untouched, so every site
            // still using the side-files keeps working exactly as before.
            if (window.ExologPageJson === true) {
                console.log(
                    "🌍 Page JSON is enabled for this Edition - legacy translation loader standing down"
                );
                return;
            }

            console.log("🚀 ExologTranslation.init() starting...");

            this.detectEditMode();
            this.detectLanguageFromUrl();

            if (
                this.currentLanguage &&
                this.currentLanguage !== this.defaultLanguage
            ) {
                // ALWAYS check if translation files exist first (regardless of localStorage)
                if (this.isEditMode) {
                    console.log(
                        "🔍 Edit mode detected - checking if translation files exist..."
                    );

                    // Wait a bit for the editor to initialize before checking files
                    setTimeout(() => {
                        this.checkIfTranslationFilesExist().then(
                            (filesExist) => {
                                if (filesExist) {
                                    // In edit mode with files existing:
                                    // Apply translations directly from scoped JSON files.
                                    // This uses the same safe TinyMCE-aware logic as in view mode,
                                    // and only calls the backend when JSON files are missing.
                                    console.log(
                                        "✅ Translation files exist - applying translations from JSON in edit mode"
                                    );
                                    this.modalDismissed = true;
                                    this.modalVisible = false;
                                    this.loadUserPreferences();
                                    // Apply translations from existing JSON files
                                    // Use silent loader mode (no "translating content" message)
                                    this.loadAndApplyTranslations(false, true);
                                } else {
                                    // Nothing translated yet - and that is not a
                                    // reason to interrupt anybody.
                                    //
                                    // This opened the Translation Setup dialogue
                                    // on every visit, which is exactly what Pim
                                    // reported: "I get the old translation modal
                                    // there for some reason, I haven't pressed
                                    // anything." Starting a translation is now a
                                    // deliberate act - the Auto-translate button
                                    // in the toolbar - which is what he asked
                                    // for: "perhaps both and the button as a
                                    // fallback".
                                    console.log(
                                        "ℹ️ No translation files yet - use the Auto-translate button to start"
                                    );
                                    this.modalDismissed = true;
                                    this.modalVisible = false;
                                    console.log(
                                        "⏸️ init() complete - waiting for modal interaction"
                                    );
                                }
                            }
                        );
                    }, 300);
                } else {
                    // View mode: Apply translations directly
                    console.log(
                        "✅ Not in edit mode - starting translations immediately"
                    );
                    this.modalDismissed = true; // No modal needed
                    this.modalVisible = false;
                    this.loadUserPreferences();
                    this.loadAndApplyTranslations();
                }
            } else {
                console.log(
                    "ℹ️ Default language or no language detected - no translations needed"
                );
            }
        },

        /**
         * Detect if user is in edit mode (admin logged in)
         */
        detectEditMode: function () {
            // Check for Exolog Editor meta tag
            const editorMeta = document.querySelector(
                'meta[name="exologEditor"]'
            );
            this.isEditMode = !!editorMeta;
            console.log(
                "🔐 Edit mode:",
                this.isEditMode ? "ENABLED" : "DISABLED",
                "| Meta tag found:",
                !!editorMeta
            );
        },

        /**
         * Check if user has already made translation preferences
         */
        hasUserPreferences: function () {
            const storageKey = `exolog_translation_prefs_${this.currentLanguage}`;
            const prefs = localStorage.getItem(storageKey);
            return !!prefs;
        },

        /**
         * Wait for the Exolog Editor to be fully initialized
         * Polls for the 'exedit-on' class on body which indicates edit mode is active
         */
        waitForEditorReady: function () {
            return new Promise((resolve) => {
                const checkEditorReady = () => {
                    const body = document.body;
                    const isEditModeActive =
                        body.classList.contains("exedit-on");
                    const hasTinyMCE =
                        window.tinymce &&
                        window.tinymce.editors &&
                        window.tinymce.editors.length > 0;

                    if (isEditModeActive && hasTinyMCE) {
                        console.log("✅ Editor is ready - TinyMCE initialized");
                        // Add small extra delay to ensure all editors are truly ready
                        setTimeout(resolve, 200);
                    } else if (!this.isEditMode) {
                        // Not in edit mode, no need to wait
                        console.log(
                            "✅ Not in edit mode - proceeding immediately"
                        );
                        resolve();
                    } else {
                        // Check again in 100ms
                        setTimeout(checkEditorReady, 100);
                    }
                };

                // Start checking immediately
                checkEditorReady();

                // Fallback: resolve after 5 seconds regardless
                setTimeout(() => {
                    console.log("⚠️ Editor ready timeout - proceeding anyway");
                    resolve();
                }, 5000);
            });
        },

        /**
         * Check if any translation files exist for the current language
         * This prevents showing the modal if translations are already cached
         */
        checkIfTranslationFilesExist: function () {
            const self = this;

            // Get all translatable elements
            const exeditElements = document.querySelectorAll("[exedit]");
            const translateElements =
                document.querySelectorAll("[data-translate]");
            const allElements = [...exeditElements, ...translateElements];

            if (allElements.length === 0) {
                console.log("ℹ️ No translatable elements found");
                return Promise.resolve(false);
            }

            // Extract unique scope.id combinations
            const scopeIds = new Set();
            allElements.forEach((element) => {
                const exeditAttr =
                    element.getAttribute("exedit") ||
                    element.getAttribute("data-translate");
                if (!exeditAttr) return;

                try {
                    const exeditData = JSON.parse(exeditAttr);
                    const scope =
                        exeditData.scope || self.inferScope(exeditData.type);
                    const id = exeditData.id;
                    scopeIds.add(`${scope}.${id}`);
                } catch (e) {
                    // Skip invalid attributes
                }
            });

            console.log(
                `🔍 Checking for translation files for ${scopeIds.size} scopes...`
            );

            // Check if at least one translation file exists
            const checkPromises = Array.from(scopeIds).map((scopeKey) => {
                const [scope, id] = scopeKey.split(".");
                const cacheBuster = `?t=${Date.now()}`;
                const translationPath = `/sites/${
                    window.ExologSiteAlias || ""
                }/translations/${this.currentLanguage}/${scope}.${id}.${
                    this.currentLanguage
                }.json${cacheBuster}`;

                return fetch(translationPath, { method: "HEAD" })
                    .then((response) => {
                        const exists = response.ok;
                        console.log(
                            `📄 ${scopeKey}: ${
                                exists ? "✅ EXISTS" : "❌ NOT FOUND"
                            }`
                        );
                        return exists;
                    })
                    .catch(() => {
                        console.log(`📄 ${scopeKey}: ❌ NOT FOUND (error)`);
                        return false;
                    });
            });

            return Promise.all(checkPromises).then((results) => {
                const anyExist = results.some((exists) => exists);
                console.log(
                    `📊 Translation files check: ${
                        anyExist ? "✅ FOUND" : "❌ NONE FOUND"
                    }`
                );
                return anyExist;
            });
        },

        /**
         * Load user preferences from localStorage
         */
        loadUserPreferences: function () {
            const storageKey = `exolog_translation_prefs_${this.currentLanguage}`;
            const prefs = localStorage.getItem(storageKey);
            if (prefs) {
                try {
                    const parsed = JSON.parse(prefs);
                    this.translationScope = parsed.scope || "page";
                    this.fallbackBehavior = parsed.fallback || "default";
                    console.log(
                        "📋 Loaded preferences:",
                        this.translationScope,
                        "|",
                        this.fallbackBehavior
                    );
                } catch (e) {
                    console.warn("Failed to parse preferences:", e);
                }
            }
        },

        /**
         * Save user preferences to localStorage
         */
        saveUserPreferences: function () {
            const storageKey = `exolog_translation_prefs_${this.currentLanguage}`;
            const prefs = {
                scope: this.translationScope,
                fallback: this.fallbackBehavior,
                timestamp: new Date().toISOString(),
            };
            localStorage.setItem(storageKey, JSON.stringify(prefs));
            console.log("💾 Saved preferences:", prefs);
        },

        // The Translation Setup dialogue used to stand here: three methods
        // that built it, closed it and styled it. init() stopped opening it
        // when Pim reported "I get the old translation modal there for some
        // reason, I haven't pressed anything", and it has now gone entirely -
        // "all other modals about the translation should not appear any
        // longer". Starting a translation is the toolbar button or the offer
        // dialogue, and nothing else.

        /**
         * The language this page is being shown in, however we can find out.
         *
         * init() returns early on a Page JSON Edition - "legacy translation
         * loader standing down" - so detectLanguageFromUrl() never runs there
         * and currentLanguage stays null. The bridge read that null and opened
         * the edit modal with no language at all, which the admin then treated
         * as the primary: editing a Bengali page silently offered the Dutch
         * original. The modal is used on exactly the Editions where init()
         * stands down, so it cannot depend on init() having run.
         */
        resolveLanguage: function () {
            if (this.currentLanguage) {
                return this.currentLanguage;
            }

            // The server already knows - it rendered the page.
            const params = window.ExologEditorParams || {};
            if (params.current_language) {
                return params.current_language;
            }

            // Last resort: the URL, matched against the Edition's own list
            // rather than a fixed one.
            const segments = window.location.pathname.split("/").filter((s) => s.length > 0);
            const declared = this.supportedLanguages || [];
            if (segments.length > 0 && declared.includes(segments[0])) {
                return segments[0];
            }

            // The Edition's real primary - not `defaultLanguage`, which the
            // legacy flow above still needs to read as "en".
            return params.default_language
                || (Array.isArray(declared) && declared.length ? declared[0] : null);
        },

        /**
         * Detect language from URL slug
         */
        detectLanguageFromUrl: function () {
            const path = window.location.pathname;
            const segments = path.split("/").filter((s) => s.length > 0);

            if (
                segments.length > 0 &&
                this.supportedLanguages.includes(segments[0])
            ) {
                this.currentLanguage = segments[0];
                console.log(
                    "🌍 Language detected from URL:",
                    this.currentLanguage
                );
            } else {
                this.currentLanguage = this.defaultLanguage;
            }
        },

        /**
         * Decode obfuscated ID from data-translate attribute
         */
        decodeId: function (encodedId) {
            try {
                // Decode base64
                const decoded = atob(encodedId);
                // Extract the ID (before the pipe)
                const parts = decoded.split("|");
                if (parts.length !== 2) {
                    console.warn("Invalid encoded ID format:", encodedId);
                    return encodedId; // Return as-is if format is wrong
                }
                // Reverse the ID back to original
                const id = parts[0].split("").reverse().join("");
                return parseInt(id, 10);
            } catch (e) {
                console.warn("Failed to decode ID:", encodedId, e);
                return encodedId; // Return as-is if decoding fails
            }
        },

        /**
         * Show loading indicator during translation
         * Creates a simple loader if exoAutoTranslate is not available
         * @param {boolean} silentMode - If true, show only spinner without white box (just overlay + spinner)
         */
        showLoadingIndicator: function (silentMode = false) {
            // Check if loader already exists
            let loader = document.querySelector(".ex-auto-translate-loading");

            // Remove existing loader if switching modes
            if (loader) {
                loader.remove();
                loader = null;
            }

            if (!loader) {
                // Silent mode: just overlay with centered spinner, no white box
                // Normal mode: white box with spinner and message
                const loaderHTML = silentMode
                    ? `
                    <div class="ex-auto-translate-loading" style="display: none; opacity: 0;">
                        <div class="ex-auto-translate-loading__overlay" style="
                            position: fixed;
                            top: 0;
                            left: 0;
                            width: 100%;
                            height: 100%;
                            background: rgba(255, 255, 255, 0.7);
                            z-index: 9998;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                        ">
                            <iconify-icon icon="lucide:loader-2" class="spinner-icon" style="font-size: 40px; color: #00919c;"></iconify-icon>
                        </div>
                    </div>
                    `
                    : `
                    <div class="ex-auto-translate-loading" style="display: none; opacity: 0;">
                        <div class="ex-auto-translate-loading__overlay" style="
                            position: fixed;
                            top: 0;
                            left: 0;
                            width: 100%;
                            height: 100%;
                            background: rgba(0, 0, 0, 0.5);
                            z-index: 9998;
                        "></div>
                        <div class="ex-auto-translate-loading__content" style="
                            position: fixed;
                            top: 50%;
                            left: 50%;
                            transform: translate(-50%, -50%);
                            background: white;
                            padding: 30px 50px;
                            border-radius: 8px;
                            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
                            z-index: 9999;
                            text-align: center;
                        ">
                            <iconify-icon icon="lucide:loader-2" class="spinner-icon" style="font-size: 32px; color: #00919c; margin-bottom: 15px; display: block;"></iconify-icon>
                            <p style="margin: 0; font-size: 16px; color: #333;">Translating content...</p>
                        </div>
                    </div>
                    `;
                document.body.insertAdjacentHTML("beforeend", loaderHTML);
                loader = document.querySelector(".ex-auto-translate-loading");
            }

            // Show the loader with fade in
            if (loader) {
                loader.style.display = "block";
                setTimeout(() => {
                    loader.style.opacity = "1";
                    loader.style.transition = "opacity 0.2s ease";
                }, 10);
                console.log("⏳ Loading indicator shown", silentMode ? "(silent mode - no white box)" : "(with message)");
            }
        },

        /**
         * Hide loading indicator
         */
        hideLoadingIndicator: function () {
            const loader = document.querySelector(".ex-auto-translate-loading");

            if (loader) {
                loader.style.opacity = "0";
                setTimeout(() => {
                    loader.style.display = "none";
                    console.log("✅ Loading indicator hidden");
                }, 200);
            }
        },

        /**
         * Load and apply scoped translations
         * @param {boolean} skipApplyInEditMode - If true and in edit mode, only create files, don't apply
         * @param {boolean} useSilentLoader - If true, use silent loader (no "translating content" message)
         */
        loadAndApplyTranslations: function (skipApplyInEditMode = false, useSilentLoader = false) {
            const self = this;

            // Clear in-memory cache to ensure we load fresh data from JSON files
            this.translations = {};

            // Log call stack to trace where this is being called from
            console.log(
                "🔍 loadAndApplyTranslations() called from:",
                new Error().stack
            );

            // CRITICAL: Block ALL translations if modal is visible OR not dismissed
            if (this.modalVisible) {
                console.error(
                    "🚫 BLOCKED: Modal is still visible! Translations MUST wait.",
                    {
                        modalVisible: this.modalVisible,
                        modalDismissed: this.modalDismissed,
                    }
                );
                return;
            }

            if (
                !this.modalDismissed &&
                this.isEditMode &&
                !this.hasUserPreferences()
            ) {
                console.error(
                    "🚫 BLOCKED: Modal not dismissed yet! Translations MUST wait.",
                    {
                        isEditMode: this.isEditMode,
                        modalDismissed: this.modalDismissed,
                        hasPreferences: this.hasUserPreferences(),
                    }
                );
                return;
            }

            console.log("✅ Translation process APPROVED - starting now", {
                isEditMode: this.isEditMode,
                modalDismissed: this.modalDismissed,
                modalVisible: this.modalVisible,
                hasPreferences: this.hasUserPreferences(),
                language: this.currentLanguage,
                useSilentLoader: useSilentLoader,
            });

            // Show loading indicator (silent mode hides the "translating content" message)
            this.showLoadingIndicator(useSilentLoader);

            // Extract all translatable elements (both exedit and data-translate)
            // In edit mode: elements have [exedit] attributes
            // In view mode: elements have [data-translate] attributes
            const exeditElements = document.querySelectorAll("[exedit]");
            const translateElements =
                document.querySelectorAll("[data-translate]");
            const allElements = [...exeditElements, ...translateElements];
            const scopeGroups = {}; // { 'page.123': [...elements], 'site.1': [...elements] }

            console.log(
                "🔍 Found translatable elements:",
                allElements.length,
                "(exedit:",
                exeditElements.length,
                "data-translate:",
                translateElements.length + ")"
            );

            allElements.forEach((element) => {
                // Try both attribute types
                const exeditAttr =
                    element.getAttribute("exedit") ||
                    element.getAttribute("data-translate");
                if (!exeditAttr) return;

                try {
                    const exeditData = JSON.parse(exeditAttr);

                    // Decode ID if it's from data-translate attribute (encoded)
                    if (
                        element.hasAttribute("data-translate") &&
                        exeditData.id &&
                        typeof exeditData.id === "string"
                    ) {
                        exeditData.id = self.decodeId(exeditData.id);
                    }

                    // Skip non-translatable content types
                    const skipContentTypes = [
                        "picture",
                        "file",
                        "video",
                        "audio",
                    ];
                    if (skipContentTypes.includes(exeditData.contenttype)) {
                        return;
                    }

                    // Skip container elements (elements that have other translatable children)
                    const hasExeditChildren = element.querySelector(
                        "[exedit], [data-translate]"
                    );
                    if (hasExeditChildren) {
                        return;
                    }

                    const scope =
                        exeditData.scope || self.inferScope(exeditData.type);
                    const id = exeditData.id;
                    const scopeKey = `${scope}.${id}`;

                    if (!scopeGroups[scopeKey]) {
                        scopeGroups[scopeKey] = [];
                    }
                    scopeGroups[scopeKey].push({ element, exeditData });
                } catch (e) {
                    console.warn("Failed to parse exedit attribute:", e);
                }
            });

            console.log("📦 Grouped into scopes:", Object.keys(scopeGroups));

            // Load translation files for each scope
            const loadPromises = Object.keys(scopeGroups).map((scopeKey) => {
                const [scope, id] = scopeKey.split(".");
                return self.loadScopedTranslationFile(scope, id);
            });

            // Return the promise chain so callers can react when translations are done
            return Promise.all(loadPromises)
                .then(() => {
                    console.log("✅ All scoped translation files checked");

                    // Check if any translations are missing (null values)
                    const missingScopes = Object.keys(scopeGroups).filter(
                        (scopeKey) => self.translations[scopeKey] === null
                    );

                    if (missingScopes.length > 0) {
                        // Check if user is in edit mode (admin logged in)
                        const isEditMode = document.querySelector(
                            'meta[name="exologEditor"]'
                        );

                        if (isEditMode) {
                            console.log(
                                `🔄 Missing translations for ${missingScopes.length} scopes, calling API...`
                            );
                            // Translate missing scopes via API (only in edit mode)
                            return self.translateMissingScopes(
                                scopeGroups,
                                missingScopes
                            );
                        } else {
                            console.log(
                                `⚠️ Missing translations for ${missingScopes.length} scopes, but not in edit mode. Skipping API call.`
                            );
                            // Initialize empty translations for missing scopes
                            missingScopes.forEach((scopeKey) => {
                                self.translations[scopeKey] = {};
                            });
                        }
                    }
                })
                .then(() => {
                    // In edit mode with skipApply flag: only create files, let exoAutoTranslate apply
                    if (skipApplyInEditMode && self.isEditMode) {
                        console.log(
                            "✅ Translation files created - letting exoAutoTranslate apply translations"
                        );
                        console.log(
                            "ℹ️ Trigger exoAutoTranslate by clicking the translate button or reloading"
                        );
                        self.hideLoadingIndicator();
                        return;
                    }

                    // Apply all translations (both loaded and newly translated)
                    // Add a delay to ensure TinyMCE editors are fully initialized
                    setTimeout(() => {
                        console.log(
                            "🔄 Applying translations to page elements..."
                        );
                        self.applyTranslations(scopeGroups);
                        // Hide loading indicator after translations are applied
                        self.hideLoadingIndicator();
                    }, 800);
                })
                .catch((error) => {
                    console.warn("⚠️ Translation error:", error);
                    // In edit mode with skipApply flag: don't apply on error either
                    if (skipApplyInEditMode && self.isEditMode) {
                        console.log(
                            "⚠️ Error creating translation files - exoAutoTranslate will retry"
                        );
                        self.hideLoadingIndicator();
                        return;
                    }
                    // Still try to apply whatever translations we have
                    self.applyTranslations(scopeGroups);
                    // Hide loading indicator even on error
                    self.hideLoadingIndicator();
                });
        },

        /**
         * Load a single scoped translation file
         * If file doesn't exist, trigger API translation to create it
         */
        loadScopedTranslationFile: function (scope, id) {
            const self = this;
            const scopeKey = `${scope}.${id}`;
            // Add cache buster to bypass browser cache after manual edits
            const cacheBuster = `?t=${Date.now()}`;
            const translationPath = `/sites/${
                window.ExologSiteAlias || ""
            }/translations/${this.currentLanguage}/${scope}.${id}.${
                this.currentLanguage
            }.json${cacheBuster}`;

            console.log(`📥 Loading translation file:`, {
                path: translationPath,
                scope,
                id,
                language: this.currentLanguage,
                fullURL: window.location.origin + translationPath,
            });

            return fetch(translationPath)
                .then((response) => {
                    if (!response.ok) {
                        // File doesn't exist - need to translate via API
                        console.log(
                            `📝 Translation file not found, will translate via API: ${scopeKey}`
                        );
                        return null; // Signal that we need to translate
                    }
                    return response.json();
                })
                .then((translations) => {
                    if (translations) {
                        // Successfully loaded from file
                        self.translations[scopeKey] = translations;
                        console.log(
                            `✅ Loaded ${scopeKey}:`,
                            Object.keys(translations).length,
                            "fields"
                        );
                    } else {
                        // Mark as needing translation
                        self.translations[scopeKey] = null;
                    }
                })
                .catch((error) => {
                    console.warn(
                        `⚠️ Could not load ${scopeKey}:`,
                        error.message
                    );
                    self.translations[scopeKey] = null; // Mark as needing translation
                });
        },

        /**
         * Translate missing scopes via API
         */
        translateMissingScopes: function (scopeGroups, missingScopes) {
            const self = this;

            // Collect all content items that need translation
            const contentItems = [];

            missingScopes.forEach((scopeKey) => {
                const items = scopeGroups[scopeKey];
                items.forEach((item) => {
                    const { element, exeditData } = item;
                    const content = element.innerHTML;

                    // Skip empty or whitespace-only content
                    if (!content || !content.trim()) {
                        console.log(
                            `⚠️ Skipping empty content for ${exeditData.type}.${exeditData.id}.${exeditData.field}`
                        );
                        return;
                    }

                    contentItems.push({
                        type: exeditData.type,
                        id: exeditData.id,
                        field: exeditData.field || "_",
                        key: exeditData.key || null,
                        scope:
                            exeditData.scope ||
                            self.inferScope(exeditData.type),
                        content: content,
                        timestamp:
                            exeditData.timestamp || new Date().toISOString(),
                    });
                });
            });

            console.log(
                `🌐 Translating ${contentItems.length} items via API...`
            );

            // If no valid content to translate, skip API call
            if (contentItems.length === 0) {
                console.log(
                    `⚠️ No valid content to translate. Skipping API call.`
                );
                // Initialize empty translations for missing scopes
                missingScopes.forEach((scopeKey) => {
                    self.translations[scopeKey] = {};
                });
                return Promise.resolve();
            }

            // Call the API
            return fetch("/api/translation/autoTranslateScoped", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
                body: JSON.stringify({
                    content: contentItems,
                    target_language: this.currentLanguage,
                }),
            })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`API error: ${response.status}`);
                    }
                    return response.json();
                })
                .then((apiResponse) => {
                    // API wraps response in { success: true, data: { translations: [...] } }
                    console.log("🔍 API Response:", apiResponse);

                    const data = apiResponse.data || apiResponse;
                    const translations = data.translations || [];

                    console.log(
                        `✅ API translated ${translations.length} items`
                    );

                    // Store translations by scope
                    translations.forEach((item) => {
                        const scope = item.scope || self.inferScope(item.type);
                        const scopeKey = `${scope}.${item.id}`;

                        if (!self.translations[scopeKey]) {
                            self.translations[scopeKey] = {};
                        }

                        // Use storage key (field.key) if key exists, otherwise just field
                        const storageKey =
                            item.key && item.key !== "null"
                                ? `${item.field}.${item.key}`
                                : item.field;

                        self.translations[scopeKey][storageKey] = {
                            translated: item.translated,
                            source: item.source,
                            published: item.published ?? false,
                        };
                    });

                    console.log(
                        "📦 Stored translations:",
                        Object.keys(self.translations)
                    );
                })
                .catch((error) => {
                    console.error("❌ API translation failed:", error);
                    // Initialize empty translations for missing scopes
                    missingScopes.forEach((scopeKey) => {
                        if (self.translations[scopeKey] === null) {
                            self.translations[scopeKey] = {};
                        }
                    });
                });
        },

        /**
         * Infer scope from type if not explicitly set
         */
        inferScope: function (type) {
            if (type === "react") return "page";
            if (type === "var") return "site";
            if (type === "form") return "form";
            return "page"; // default
        },

        /**
         * Open Edit Modal via exoBridge (the standard way to open Vue modals from exo_editor)
         * This opens the React edit view in an iframe with the translation view active
         * @param {string} reactId - The react_id to open the modal with
         * @param {string} initialView - The initial view to show ('form', 'translation', or 'meta')
         */
        openEditModalViaBridge: function (reactId, initialView) {
            const self = this;

            // Import exoBridge dynamically since it's part of exo_editor
            import("@/common/exoBridge").then((module) => {
                const exoBridge = module.default;

                console.log(
                    "📝 Opening Edit Modal via exoBridge...",
                    { reactId, initialView, language: self.currentLanguage }
                );

                // Use exoBridge to open the React edit route with query parameters
                // The route name R_REACT_EDIT maps to /reacts/:react_id
                exoBridge.open({
                    name: 'R_REACT_EDIT',
                    params: { react_id: String(reactId) },
                    query: {
                        // Pim asked for the unified form in the edit modal, so
                        // that is what is requested when no view is named. The
                        // translation and meta views remain one toolbar click
                        // away inside the modal.
                        initialView: initialView || 'form',
                        initialLanguage: self.resolveLanguage()
                    }
                });
            }).catch((error) => {
                console.error("❌ Failed to load exoBridge:", error);
                // Fallback to old translation modal
                if (
                    window.ExologLanguageIcon &&
                    window.ExologLanguageIcon.data &&
                    window.ExologLanguageIcon.data.translationModal &&
                    typeof window.ExologLanguageIcon.data.translationModal.open === "function"
                ) {
                    console.log("📝 Falling back to translation review modal...");
                    window.ExologLanguageIcon.data.translationModal.open();
                }
            });
        },

        /**
         * Alias for backward compatibility - calls openEditModalViaBridge
         * @param {string} reactId - The react_id to open the modal with
         */
        waitForEditModal: function (reactId) {
            // Opened straight after a first-time translation. It used to land on
            // the translation view, which is exactly what Pim reported: "when I
            // am on this page I still get the Translate Modal". The form shows
            // the translated page itself, which is what he wants to see.
            this.openEditModalViaBridge(reactId, 'form');
        },

        /**
         * Get the first react_id from the page elements
         * Used to open the Edit Modal after first-time translation
         */
        getFirstReactIdFromPage: function () {
            // Find the first exedit element with type="react"
            const exeditElements = document.querySelectorAll("[exedit]");
            for (const element of exeditElements) {
                try {
                    const exeditAttr = element.getAttribute("exedit");
                    if (!exeditAttr) continue;
                    const exeditData = JSON.parse(exeditAttr);
                    if (exeditData.type === "react" && exeditData.id) {
                        console.log("🔍 Found first react_id:", exeditData.id);
                        return exeditData.id;
                    }
                } catch (e) {
                    // Skip invalid attributes
                }
            }
            console.warn("⚠️ No react_id found on page");
            return null;
        },

        /**
         * Apply translations to all exedit elements
         * Handles TinyMCE editors and textareas specially to preserve functionality
         */
        applyTranslations: function (scopeGroups) {
            const self = this;
            let appliedCount = 0;
            let totalElements = 0;
            let skippedCount = 0;

            // Content types that should NOT have translations applied (binary/media content)
            const skipContentTypes = [
                "picture",
                "file",
                "video",
                "audio",
                "image",
            ];

            // Iterate through each scope group
            Object.keys(scopeGroups).forEach((scopeKey) => {
                const items = scopeGroups[scopeKey];
                const translations = self.translations[scopeKey] || {};

                items.forEach((item) => {
                    totalElements++;
                    const { element, exeditData } = item;

                    // Skip non-text content types
                    if (
                        exeditData.contenttype &&
                        skipContentTypes.includes(exeditData.contenttype)
                    ) {
                        console.log(
                            `⏭️ Skipping non-text content type: ${exeditData.contenttype}`
                        );
                        skippedCount++;
                        return;
                    }

                    // Skip elements that contain nested editable elements
                    // Replacing their innerHTML would destroy nested editors
                    const $element =
                        window.jQuery || window.$
                            ? (window.jQuery || window.$)(element)
                            : null;
                    if ($element && $element.find("[exedit]").length > 0) {
                        console.log(
                            `⏭️ Skipping element with nested editables`
                        );
                        skippedCount++;
                        return;
                    }

                    const translatedText = self.getTranslation(
                        exeditData,
                        translations
                    );

                    if (translatedText) {
                        console.log(`🔍 Looking for translation for element:`, {
                            field: exeditData.field,
                            key: exeditData.key,
                            id: exeditData.id,
                            elementId: element.id,
                            storageKey: exeditData.key
                                ? `${exeditData.field}.${exeditData.key}`
                                : exeditData.field,
                            translatedText:
                                translatedText.substring(0, 50) + "...",
                        });

                        // Check if element has TinyMCE inline editor attached
                        // TinyMCE inline editors are stored using the element's ID or a generated ID
                        let tinyMCEEditor = null;

                        if (window.tinymce) {
                            // Try by element ID first
                            if (element.id) {
                                tinyMCEEditor = window.tinymce.get(element.id);
                            }

                            // If not found, try to find by matching the target element
                            if (!tinyMCEEditor) {
                                const editors = window.tinymce.editors || [];
                                for (let i = 0; i < editors.length; i++) {
                                    const ed = editors[i];
                                    if (
                                        ed.targetElm === element ||
                                        ed.getElement() === element ||
                                        (ed.id &&
                                            element.id &&
                                            ed.id === element.id)
                                    ) {
                                        tinyMCEEditor = ed;
                                        break;
                                    }
                                }
                            }

                            // If still not found, search all editors by comparing their target elements
                            if (!tinyMCEEditor) {
                                (window.tinymce.editors || []).forEach((ed) => {
                                    if (
                                        ed.getElement() === element ||
                                        ed.targetElm === element ||
                                        (element.id && ed.id === element.id)
                                    ) {
                                        tinyMCEEditor = ed;
                                    }
                                });
                            }
                        }

                        // Check if element is a textarea
                        const isTextarea =
                            element.tagName.toLowerCase() === "textarea";

                        if (tinyMCEEditor) {
                            // Use TinyMCE API to set content without destroying editor
                            tinyMCEEditor.setContent(translatedText);

                            // CRITICAL: Update startContent to the translated content
                            // This ensures unsaved-changes detection compares against the JSON file content
                            // instead of the original database content
                            tinyMCEEditor.startContent =
                                tinyMCEEditor.getContent({
                                    format: "raw",
                                });

                            // Also reset the undo manager to prevent false dirty state
                            if (tinyMCEEditor.undoManager) {
                                tinyMCEEditor.undoManager.clear();
                                tinyMCEEditor.undoManager.add();
                            }

                            // Mark as clean
                            tinyMCEEditor.setDirty(false);

                            console.log(
                                `📝 Applied translation to TinyMCE: ${tinyMCEEditor.id}`
                            );
                        } else if (isTextarea) {
                            // For textareas, set value instead of innerHTML
                            element.value = translatedText.replace(
                                /<[^>]*>/g,
                                ""
                            ); // Strip HTML for textarea
                            console.log(`📝 Applied translation to textarea`);
                        } else {
                            // For regular elements, use innerHTML
                            // But if it's a contenteditable element (TinyMCE might not be initialized yet),
                            // try to update it carefully
                            if (
                                element.hasAttribute("contenteditable") &&
                                element.contenteditable === "true"
                            ) {
                                // For contenteditable elements, try to update text content first
                                // This won't break TinyMCE if it initializes later
                                try {
                                    element.textContent =
                                        translatedText.replace(/<[^>]*>/g, "");
                                    console.log(
                                        `📝 Applied translation to contenteditable element (text only): ${
                                            element.id || "no-id"
                                        }`
                                    );
                                } catch (e) {
                                    console.warn(
                                        `⚠️ Failed to update contenteditable element:`,
                                        e
                                    );
                                    // Fallback to innerHTML
                                    element.innerHTML = translatedText;
                                }
                            } else {
                                // For regular elements, use innerHTML
                                element.innerHTML = translatedText;
                            }
                            console.log(
                                `📝 Applied translation to regular element: ${element.tagName}`
                            );
                        }
                        appliedCount++;
                    } else {
                        console.warn(`⚠️ No translation found for:`, {
                            field: exeditData.field,
                            key: exeditData.key,
                            scopeKey: scopeKey,
                            availableKeys: Object.keys(translations),
                        });
                    }
                });
            });

            console.log(
                `✅ Applied ${appliedCount} translations out of ${totalElements} elements (skipped ${skippedCount})`
            );
        },

        /**
         * Get translation for a specific exedit element from scoped translations
         * In view mode, only returns published translations
         * In edit mode, returns all translations
         */
        getTranslation: function (exeditData, scopedTranslations) {
            const field = exeditData.field || "_";
            const property = exeditData.property;
            const key = exeditData.key === "null" ? null : exeditData.key;

            // Build the storage key (matches backend format)
            // If there's a key, use "field.key" format, otherwise just "field"
            const storageKey = key ? `${field}.${key}` : field;

            console.log(`🔍 [getTranslation] Looking for ${storageKey}`, {
                field,
                key,
                hasTranslations: !!scopedTranslations,
                availableKeys: scopedTranslations
                    ? Object.keys(scopedTranslations)
                    : [],
            });

            // Try to get translation using the storage key
            let translationData = scopedTranslations[storageKey];
            if (translationData && translationData.translated) {
                console.log(`✅ [getTranslation] Found:`, {
                    storageKey,
                    translated: translationData.translated.substring(0, 30),
                });
                // Check published flag in view mode
                if (!this.isEditMode && translationData.published === false) {
                    return null;
                }
                return translationData.translated;
            }

            // Fallback: try just the field name
            let fieldData = scopedTranslations[field];
            if (fieldData && fieldData.translated) {
                console.log(`✅ [getTranslation] Found via fallback (field):`, {
                    field,
                    translated: fieldData.translated.substring(0, 30),
                });
                if (!this.isEditMode && fieldData.published === false) {
                    return null;
                }
                return fieldData.translated;
            }

            console.warn(`❌ [getTranslation] NOT FOUND: ${storageKey}`);
            return null;
        },
    };

    // Initialize when DOM is ready
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", function () {
            ExologTranslation.init();
        });
    } else {
        ExologTranslation.init();
    }

    // Expose to window for debugging
    window.ExologTranslation = ExologTranslation;
})();
